import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import {
  ChipInputSearchService,
  IChipSearchFilterElement,
  IChipsSearchFilterSection,
  SECTIONS,
} from '@el-chip-input-search';
import { isEmpty, isEqual } from 'lodash';
import * as _ from 'lodash';
import {
  BehaviorSubject,
  debounceTime,
  filter,
  firstValueFrom,
  Observable,
  take,
  takeUntil,
} from 'rxjs';

import { IFrequentlyUsedChips, IKeyValuePair } from '@shared/interfaces';

import { PersonalizationService } from '../pages/personalization/services/personalization.service';

@Injectable({ providedIn: 'root' })
export class CISService {
  private allPossibleKeys = new BehaviorSubject<string[]>([]);
  private chipsData = new BehaviorSubject<IChipsSearchFilterSection[]>([]);
  private oldQueryParams: Params;
  private THRESHOLD_MULTIPLIER = 0.75;
  private BASE_FREQUENCY = 50;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    @Inject(ChipInputSearchService)
    private chipInputSearchService: ChipInputSearchService,
    @Inject(PersonalizationService)
    private personalizationService: PersonalizationService
  ) {}

  initCIS(
    allPossibleKeys: string[],
    chipsData: IChipsSearchFilterSection[],
    onDestroy$: Observable<void>,
    onSearchClicked: (search?: boolean) => void
  ) {
    this.allPossibleKeys.next(allPossibleKeys);
    this.chipsData.next(chipsData);

    this.setChipsToCIS();

    this.chipInputSearchService.onSearchClicked
      .pipe(takeUntil(onDestroy$))
      .subscribe(() => {
        if (this.chipInputSearchService.loading) {
          onSearchClicked();
        }
      });

    const reInitParams = () => {
      this.setChipsToCIS();

      const { queryParams } = this.route.snapshot;

      if (!isEmpty(queryParams) || !isEqual(queryParams, this.oldQueryParams)) {
        this.oldQueryParams = queryParams;

        if (!isEmpty(queryParams) && queryParams.is_search === 'true')
          onSearchClicked(true);
        else onSearchClicked(false);
      }
    };

    reInitParams();
    this.router.events
      .pipe(takeUntil(onDestroy$), debounceTime(100))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          reInitParams();
        }
      });

    firstValueFrom(onDestroy$).then(() => {
      this.onDestroy();
    });
  }

  private setChipsToCIS() {
    const { queryParams } = this.route.snapshot;

    this.chipInputSearchService.setChipSearchFilterData(
      this.allPossibleKeys.getValue(),
      queryParams,
      this.chipsData.getValue()
    );
  }

  public getSearchParams(start?: number, size?: number) {
    const { httpParams, queryParams } = this.chipInputSearchService.getFilters(
      start ?? 0,
      size ?? 0
    );
    const onFinishSearch = async (options?: {
      replaceUrl?: boolean;
      modifiedQueryParams?: any;
    }) => {
      this.oldQueryParams = Object.entries(
        options?.modifiedQueryParams ?? queryParams ?? {}
      ).reduce((_queryParams, [key, value]) => {
        if (value) {
          _queryParams[key] = value;
        }

        return _queryParams;
      }, {} as any);

      const currentQueryParams = this.route.snapshot.queryParams;

      const newQueryParams = Object.fromEntries(
        Object.entries(queryParams).filter(([key, value]) => key && value)
      );

      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          take(1) // Take only the first NavigationEnd event after the navigation
        )
        .subscribe(() => {
          this.setChipsToCIS();
        });

      this.router.navigate([], {
        relativeTo: this.route,
        queryParamsHandling: 'merge',
        queryParams,
        replaceUrl: options.replaceUrl,
      });

      if (_.isEqual(currentQueryParams, newQueryParams)) {
        this.setChipsToCIS();
      }
    };

    return { httpParams, queryParams, onFinishSearch };
  }

  private onDestroy() {
    this.oldQueryParams = undefined;
    this.chipInputSearchService.resetChipSearchFilter();
    this.allPossibleKeys.next([]);
    this.chipsData.next([]);
  }

  /**
   * * This method uses for getting frequently used CIS chips
   *
   * @param moduleKey key name of the module
   * @returns Promise<IFrequentlyUsedChips>
   * @since 1.274.0
   */

  public getFrequentlyUsedChipsFor(
    moduleKey: string
    // callback: (filteredModuleData: IFrequentlyUsedChips) => void
  ): Promise<IFrequentlyUsedChips> {
    return new Promise((resolve) => {
      this.personalizationService
        .getPersonalizationData()
        .pipe(take(1))
        .subscribe((personalizationData) => {
          if (personalizationData.data?.frequently_used_chips?.length > 0) {
            const filteredModuleData =
              personalizationData.data?.frequently_used_chips.filter(
                (moduleData) => moduleData.module === moduleKey
              );
            if (filteredModuleData && filteredModuleData.length == 1)
              resolve(filteredModuleData[0]);
            else resolve(undefined);
          } else {
            resolve(undefined);
          }
        });
    });
  }

  /**
   * * This method uses for getting frequently used CIS chips
   *
   * @returns Promise<IFrequentlyUsedChips>
   * @since 1.274.0
   */

  public getAllFrequentlyUsedChipsFor(): Promise<IFrequentlyUsedChips[]> {
    return new Promise((resolve) => {
      this.personalizationService
        .getPersonalizationData()
        .pipe(take(1))
        .subscribe((personalizationData) => {
          if (personalizationData.data?.frequently_used_chips?.length > 0) {
            const filteredModuleData =
              personalizationData.data?.frequently_used_chips;
            resolve(filteredModuleData);
          } else {
            resolve([]);
          }
        });
    });
  }

  /**
   * * This method uses for setting frequently used CIS chips
   *
   * @param moduleKey key name of the module
   * @param usedChipsList current used chips list from CIS bar
   * @returns void
   * @since 1.274.0
   */

  public setFrequentlyUsedChipsFor(moduleKey: string, usedChipsList: string[]) {
    this.getFrequentlyUsedChipsFor(moduleKey).then((response) => {
      if (usedChipsList.length === 0) return;
      if (!response) {
        const frequentlyUsedChips: IFrequentlyUsedChips = {
          module: moduleKey,
          chips: usedChipsList.map((usedChip) => {
            return { key: usedChip, value: 1 } as IKeyValuePair<number>;
          }),
        };
        this.getAllFrequentlyUsedChipsFor().then(
          (restChipsList: IFrequentlyUsedChips[]) => {
            const updatedFrequentlyUsedChips = restChipsList
              .map((restChip) => {
                if (restChip.module === frequentlyUsedChips.module) {
                  return frequentlyUsedChips;
                }
                return restChip;
              })
              .filter(Boolean);

            const _updatedFrequentlyUsedChips =
              updatedFrequentlyUsedChips.filter(
                (chip) => chip.module === frequentlyUsedChips.module
              );

            if (_updatedFrequentlyUsedChips.length != 1) {
              updatedFrequentlyUsedChips.push(frequentlyUsedChips);
            }

            this.personalizationService
              .updatePersonalizationData({
                frequently_used_chips: updatedFrequentlyUsedChips,
              })
              .pipe(take(1))
              .subscribe();
          }
        );
      } else {
        const toBeUpdatedList = response.chips.map((oldChip) => {
          const existChip = usedChipsList.filter(
            (usedChip) => usedChip === oldChip.key
          );
          if (existChip && existChip.length === 1) {
            const value = ++oldChip.value;
            return {
              key: oldChip.key,
              value,
            };
          } else {
            return {
              key: oldChip.key,
              value: oldChip.value,
            };
          }
        });
        const allOldChips = response.chips.map((oldChip) => oldChip.key);
        const allCISChips = [...allOldChips, ...usedChipsList];
        const newChipsListWithOldChips = allCISChips.filter(
          (chip) => allCISChips.indexOf(chip) === allCISChips.lastIndexOf(chip)
        );
        const newChipsList = newChipsListWithOldChips.filter(
          (newChip) => !allOldChips.includes(newChip)
        );
        if (newChipsList && newChipsList.length > 0) {
          newChipsList.forEach((newChip: string) => {
            toBeUpdatedList.push({
              key: newChip,
              value: 1,
            } as IKeyValuePair<number>);
          });
        }
        const frequentlyUsedChips: IFrequentlyUsedChips = {
          module: moduleKey,
          chips: toBeUpdatedList,
        };

        this.getAllFrequentlyUsedChipsFor().then(
          (restChipsList: IFrequentlyUsedChips[]) => {
            const updatedFrequentlyUsedChips = restChipsList
              .map((restChip) => {
                if (restChip.module === frequentlyUsedChips.module) {
                  return frequentlyUsedChips;
                }
                return restChip;
              })
              .filter(Boolean);

            const _updatedFrequentlyUsedChips =
              updatedFrequentlyUsedChips.filter(
                (chip) => chip.module === frequentlyUsedChips.module
              );

            if (_updatedFrequentlyUsedChips.length != 1) {
              updatedFrequentlyUsedChips.push(frequentlyUsedChips);
            }

            this.personalizationService
              .updatePersonalizationData({
                frequently_used_chips: updatedFrequentlyUsedChips,
              })
              .pipe(take(1))
              .subscribe();
          }
        );
      }
    });
  }

  /**
   * * This method returns the most used chips based on the frequency.
   *
   * @param moduleDataSet {IKeyValuePair<number>[]} holds the key value pair array
   * @returns {IKeyValuePair<number>[]}
   * @since 1.274.0
   */

  private getMostUsedChipsGreaterThanThreshold(
    moduleDataSet: IKeyValuePair<number>[] = []
  ): IKeyValuePair<number>[] {
    let threshold = this.BASE_FREQUENCY;
    if (moduleDataSet) {
      moduleDataSet.sort((a, b) => b.value - a.value);
      const topFilterUsageChip: IKeyValuePair<number>[] = moduleDataSet.slice(
        0,
        1
      );
      // Applying threshold algorithm
      if (topFilterUsageChip.length == 1) {
        const _threshold = Math.ceil(
          topFilterUsageChip[0].value * this.THRESHOLD_MULTIPLIER
        );

        threshold =
          _threshold > this.BASE_FREQUENCY ? _threshold : this.BASE_FREQUENCY;
      }

      return moduleDataSet.filter((moduleChip) => moduleChip.value > threshold);
    }
    return moduleDataSet;
  }

  /**
   * * Initializes the CIS with available filters. Pass relevant parameters and it will call to
   *   `initCIS()` function.
   *
   * @param allPossibleKeys {string[]} holds all possible CIS chip keys
   * @param chipsData {IChipSearchFilterElement[]} formatted chips list with module specific chips
   * @param onDestroy$ {Observable<void>} destroyable observer for destroying subscriptions
   * @param onSearchClicked {() => void} function for executing search action
   * @returns void
   * @since 1.274.0
   */

  public initializesCIS(
    allPossibleKeys: string[],
    chipsData: IChipSearchFilterElement[],
    onDestroy$: Observable<void>,
    onSearchClicked: () => void,
    moduleName: string
  ): void {
    this.getFrequentlyUsedChipsFor(moduleName).then((frequentlyUsedChips) => {
      this.prepareCISSections(frequentlyUsedChips, chipsData).then(
        (completeChipsWithSections: IChipsSearchFilterSection[]) => {
          this.initCIS(
            allPossibleKeys,
            completeChipsWithSections,
            onDestroy$,
            onSearchClicked
          );
        }
      );
    });
  }

  /**
   * * This method uses for preparing CIS sections to be called via `initCIS`
   *
   * @param frequentlyUsedChips an object of frequently used chips
   * @param chipsData all chis chips data
   * @returns completeChipsWithSections {Promise<IChipsSearchFilterSection[]>}
   * @since 1.274.0
   */
  private prepareCISSections(
    frequentlyUsedChips: IFrequentlyUsedChips,
    chipsData: IChipSearchFilterElement[]
  ): Promise<IChipsSearchFilterSection[]> {
    return new Promise((resolve) => {
      const firstThreeChips = this.getMostUsedChipsGreaterThanThreshold(
        frequentlyUsedChips?.chips
      );

      const frequentlyUsedChipsFilters = firstThreeChips
        .map((mostFrequentlyUsedChip) => {
          const filteredChips = chipsData.filter(
            (chipData: any) =>
              chipData.chipProperties?.key == mostFrequentlyUsedChip.key
          );
          if (filteredChips.length == 1) return filteredChips[0];
          return undefined;
        })
        .filter(Boolean);

      // TODO: Handle date ranges
      const completeChipsWithSections: IChipsSearchFilterSection[] = [];

      if (frequentlyUsedChipsFilters.length > 0) {
        completeChipsWithSections.push({
          section: SECTIONS.MOST_FREQUENTLY_USED,
          filters: frequentlyUsedChipsFilters,
        });
      }

      const generalChips = chipsData.filter((chipData: any) => {
        return frequentlyUsedChipsFilters.indexOf(chipData) == -1;
      });

      if (generalChips.length > 0) {
        completeChipsWithSections.push({
          section: SECTIONS.GENERAL,
          filters: generalChips,
        });
      }
      resolve(completeChipsWithSections);
    });
  }
}
